import React from 'react';
import Appearance from 'styles/Appearance.js';
import Button from 'views/Button.js';
import Utils from 'files/Utils.js';

const AccountDrawer = ({ open, onClose, customer, orders, loading, onSignOut }) => {

    const list = orders || [];

    return (
        <>
            {/* Overlay */}
            <div
                className={`overlay ${open ? 'active' : ''}`}
                onClick={onClose} />

            {/* Drawer */}
            <div style={{
                position: 'fixed',
                top: 0,
                right: 0,
                width: Utils.isMobile() ? '100%' : 460,
                height: '100vh',
                backgroundColor: Appearance.colors.white,
                zIndex: 1000,
                transform: open ? 'translateX(0)' : 'translateX(100%)',
                transition: 'transform 0.35s ease',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: open ? '-4px 0 30px rgba(0,0,0,0.1)' : 'none',
            }}>

                {/* Header */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '20px 24px',
                    borderBottom: `1px solid ${Appearance.colors.border()}`,
                }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span style={Appearance.textStyles.subHeading()}>
                            {customer && customer.firstName ? `Hi, ${customer.firstName}` : 'Your Account'}
                        </span>
                        {customer && customer.email && (
                            <span style={Appearance.textStyles.caption()}>
                                {customer.email}
                            </span>
                        )}
                    </div>
                    <div
                        onClick={onClose}
                        style={{
                            width: 36,
                            height: 36,
                            borderRadius: 18,
                            backgroundColor: Appearance.colors.offWhite,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                        }}>
                        <i className={'fas fa-times'} style={{ fontSize: 14, color: Appearance.colors.darkGrey }} />
                    </div>
                </div>

                {/* Orders */}
                <div style={{
                    flexGrow: 1,
                    overflowY: 'auto',
                    padding: 24,
                }}>
                    <span style={{
                        ...Appearance.textStyles.body({ fontWeight: 600, color: Appearance.colors.text() }),
                        display: 'block',
                        marginBottom: 16,
                    }}>
                        {'Order History'}
                    </span>

                    {loading ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '40px 0' }}>
                            <i className={'fas fa-circle-notch fa-spin'} style={{ fontSize: 18, color: Appearance.colors.lightGrey }} />
                            <span style={Appearance.textStyles.caption()}>{'Loading your orders…'}</span>
                        </div>
                    ) : list.length === 0 ? (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 14,
                            padding: '48px 0',
                        }}>
                            <i className={'fas fa-box-open'} style={{ fontSize: 44, color: Appearance.colors.lighterGrey }} />
                            <span style={Appearance.textStyles.body({ textAlign: 'center' })}>
                                {'You have no orders yet'}
                            </span>
                        </div>
                    ) : (
                        list.map((order, index) => (
                            <OrderCard
                                key={order.id || index}
                                order={order}
                                isLast={index === list.length - 1} />
                        ))
                    )}
                </div>

                {/* Footer */}
                <div style={{
                    padding: 24,
                    borderTop: `1px solid ${Appearance.colors.border()}`,
                }}>
                    <Button
                        label={'Sign Out'}
                        type={'outline'}
                        icon={'fas fa-sign-out-alt'}
                        onClick={onSignOut}
                        style={{ width: '100%' }} />
                </div>
            </div>
        </>
    );
};

const OrderCard = ({ order, isLast }) => {

    const date = order.processedAt ? new Date(order.processedAt).toLocaleDateString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
    }) : '';

    // Flatten tracking entries across all fulfillments
    const tracking = (order.fulfillments || []).reduce((acc, f) => acc.concat(f.tracking || []), []);

    return (
        <div style={{
            paddingBottom: isLast ? 0 : 20,
            marginBottom: isLast ? 0 : 20,
            borderBottom: isLast ? 'none' : `1px solid ${Appearance.colors.border()}`,
        }}>
            {/* Order header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                <span style={Appearance.textStyles.body({ fontWeight: 600, color: Appearance.colors.text(), fontSize: 15 })}>
                    {order.name || 'Order'}
                </span>
                <span style={Appearance.textStyles.price({ fontSize: 16 })}>
                    {Utils.formatCurrency(order.total)}
                </span>
            </div>

            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12 }}>
                {date && (
                    <span style={Appearance.textStyles.caption()}>{date}</span>
                )}
                {order.financialStatus && (
                    <span style={{
                        ...Appearance.textStyles.caption(),
                        textTransform: 'capitalize',
                        padding: '2px 8px',
                        borderRadius: 10,
                        backgroundColor: Appearance.colors.offWhite,
                    }}>
                        {String(order.financialStatus).toLowerCase().replace(/_/g, ' ')}
                    </span>
                )}
            </div>

            {/* Line items */}
            <div style={{ marginBottom: tracking.length > 0 ? 12 : 0 }}>
                {(order.lineItems || []).map((item, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={Appearance.textStyles.caption({ color: Appearance.colors.text() })}>
                            {item.title}
                        </span>
                        <span style={Appearance.textStyles.caption()}>
                            {`× ${item.quantity}`}
                        </span>
                    </div>
                ))}
            </div>

            {/* Tracking */}
            {tracking.map((t, i) => (
                <a
                    key={i}
                    href={t.url || undefined}
                    target={'_blank'}
                    rel={'noopener noreferrer'}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '8px 12px',
                        marginTop: 6,
                        borderRadius: 8,
                        textDecoration: 'none',
                        backgroundColor: Appearance.colors.offWhite,
                        cursor: t.url ? 'pointer' : 'default',
                    }}>
                    <i className={'fas fa-truck'} style={{ fontSize: 12, color: Appearance.colors.primary() }} />
                    <span style={{ ...Appearance.textStyles.caption({ color: Appearance.colors.text() }), flexGrow: 1 }}>
                        {t.company ? `${t.company}: ${t.number || 'Tracking'}` : (t.number || 'Track shipment')}
                    </span>
                    {t.url && (
                        <i className={'fas fa-arrow-up-right-from-square'} style={{ fontSize: 11, color: Appearance.colors.lightGrey }} />
                    )}
                </a>
            ))}
        </div>
    );
};

export default AccountDrawer;
